import { useQConfig } from '@exam-management/feature-qconfig-provider';
import {
  GetQConfigByIdDocument,
  QConfigStructureCategory,
  useDeleteQConfigStructureMutation,
} from '@exam-management/graphql';
import { ConfirmationModal, type ConfirmationModalProps, Text } from '@shared/components';
import { useSnackbar } from '@shared/snackbar';

export interface DeleteQConfigStructureModalProps
  extends Omit<ConfirmationModalProps, 'content' | 'title'> {
  qConfigStructureId: string;
  onClose?: () => void;
}

function getActionTextByCategory(category: QConfigStructureCategory) {
  switch (category) {
    case QConfigStructureCategory.Topic:
    case QConfigStructureCategory.LearningObjectiveStatement:
      return 'Delete';
    default:
      return 'Remove';
  }
}

function getWarningTextByCategory(category: QConfigStructureCategory) {
  switch (category) {
    case QConfigStructureCategory.Part:
      return 'จะทำให้ข้อมูลการปรับแต่งข้อสอบทั้งหมด และการตั้งค่า QConfig Settings ในส่วนนี้หายไป ';
    default:
      return 'จะทำให้ข้อมูลการปรับแต่งข้อสอบทั้งหมด\nในส่วนนี้หายไป ';
  }
}

const qConfigStructureCategoryLabels: Record<string, string> = {
  [QConfigStructureCategory.Chapter]: 'Chapter',
  [QConfigStructureCategory.Course]: 'QBank',
  [QConfigStructureCategory.LearningObjectiveStatement]: 'LOS',
  [QConfigStructureCategory.Part]: 'Part',
  [QConfigStructureCategory.Subject]: 'Subject',
  [QConfigStructureCategory.Topic]: 'Topic',
};

const qConfigStructureCategoryThaiLabels: Record<string, string> = {
  [QConfigStructureCategory.Chapter]: ' (บท)',
  [QConfigStructureCategory.Course]: '',
  [QConfigStructureCategory.LearningObjectiveStatement]: '',
  [QConfigStructureCategory.Part]: ' (หมวด)',
  [QConfigStructureCategory.Subject]: ' (วิชา)',
  [QConfigStructureCategory.Topic]: ' (หัวข้อ)',
};

export default function DeleteQConfigStructureModal(
  props: Readonly<DeleteQConfigStructureModalProps>,
) {
  const { qConfigStructureId, onClose, ...restProps } = props;
  const { showSnackbar } = useSnackbar();
  const { qConfigData } = useQConfig();

  const qConfigStructure = qConfigData.structures.find(
    (structure) => structure.id === qConfigStructureId,
  );

  let qConfigStructureCategory: QConfigStructureCategory;
  if (
    qConfigStructure?.category === QConfigStructureCategory.ItemSet ||
    qConfigStructure?.category === QConfigStructureCategory.Single
  ) {
    qConfigStructureCategory = qConfigData.structures.find(
      (structure) => structure.parentId === qConfigStructureId,
    )?.category as QConfigStructureCategory;
  } else {
    qConfigStructureCategory = qConfigStructure?.category as QConfigStructureCategory;
  }

  const actionText = getActionTextByCategory(qConfigStructureCategory);

  const [deleteQConfigStructure, { loading: isDeleteQConfigStructureLoading }] =
    useDeleteQConfigStructureMutation({
      awaitRefetchQueries: true,
      refetchQueries: [
        {
          query: GetQConfigByIdDocument,
          variables: {
            getQConfigByIdInput: {
              qConfigId: qConfigData.id,
            },
          },
        },
      ],
    });

  const handleDeleteQuestionType = async () => {
    await deleteQConfigStructure({
      variables: {
        deleteQConfigStructureInput: {
          qConfigStructureId: qConfigStructureId,
          qConfigId: qConfigData.id,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: `${qConfigStructureCategoryLabels[qConfigStructureCategory]} has been ${actionText.toLowerCase()}d`,

          variant: 'success',
        });
        onClose?.();
      },
      onError: () => {
        showSnackbar({
          message: `Unable to ${actionText} ${qConfigStructureCategoryLabels[qConfigStructureCategory]}`,
          variant: 'error',
        });
      },
    });
  };

  return (
    <ConfirmationModal
      {...restProps}
      title={`${actionText} ${qConfigStructureCategoryLabels[qConfigStructureCategory]} ?`}
      content={
        <Text>
          การ {actionText} {qConfigStructureCategoryLabels[qConfigStructureCategory]}
          {qConfigStructureCategoryThaiLabels[qConfigStructureCategory]}{' '}
          <Text variant='body1Semibold' component='span'>
            {getWarningTextByCategory(qConfigStructureCategory)}
          </Text>
          กรุณาตรวจสอบให้แน่ใจก่อนดำเนินการ
        </Text>
      }
      acceptText={actionText}
      onAccept={handleDeleteQuestionType}
      onDiscard={onClose}
      onClose={onClose}
      variant='error'
      loading={isDeleteQConfigStructureLoading}
    />
  );
}
